//@ts-nocheck
import { EventType } from '@azure/msal-browser';
import ChatWidgetComponent from 'components/ConnectChatWidget';
import { Footer } from 'components/Footer';
import { LanguageCode } from 'components/LanguageSelector';
import LoadingScreen from 'components/LoadingScreen';
import LoadingScreenWithBackdrop from 'components/LoadingScreenWithBackdrop';
import ScrollToTop from 'components/ScrollToTop';
import TopNav from 'components/TopNav';
import { msalInstance } from 'config/auth';
import { oktaAuth } from 'config/oktaAuth';
import { AUTH } from 'constants/authConstants';
import { EDIT_PROFILE } from 'constants/editProfileConstants';
import {
  CONFIG,
  LANGUAGE_KEY,
  OPEN_HOME_PAGE_BANNER,
  PORTAL_VERSION,
  TRACKING_PATH,
} from 'constants/index';
import { MarketplaceContainer } from 'containers/MarketplaceContainer';
import { OnBehalfContainer } from 'containers/OnBehalfContainer';
import SupportedLanguagesContainer from 'containers/SupportedLanguagesContainer';
import { UserPreferences } from 'models';
import React, { Suspense, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Helmet from 'react-helmet';
import { NotificationContainer } from 'react-notifications';
import { useDispatch, useSelector } from 'react-redux';
import BaseAction from 'redux/actions/baseAction';
import ConfigAction from 'redux/actions/configAction';
import userPreferencesAction from 'redux/actions/userPreferencesAction';
import { RootState } from 'redux/store';
import AppService from 'services/app.service';
import UserService from 'services/userServices';
import { useContentLanguage } from 'store';
import ErrorBoundary from 'utils/ErrorBoundary';
import { loadGeInspiraFont } from 'utils/fonts';
import {
  checkEnabledFeature,
  checkNewJoinerByEmail,
  getNewJoinerInfoByEmail,
  getPermission,
  getUserProfile,
  addDecodedScriptToHead,
  handleCheckLastDate
} from 'utils/function';
import { PilotModePath } from 'utils/routes';
import './App.css';
import {
  StyledAppContainer,
  StyledContentWrapper,
  StyledBody
} from './App.style';
import { AuthenTemplate } from './AuthComponent';
import ThirdPartyScript from './ThirdPartyScript';
import queryString from 'query-string';
import { RouterProvider } from './routes';
import { initAppInsightInstance } from 'config/appInsight';
import marketplaceAction from 'redux/actions/marketplaceAction';
import { useSystemConfiguration } from 'hooks/useSystemConfiguration';
import { useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js';
import { CONFIG_CLIENT } from 'constants/configClientConstant';
import 'react-toastify/dist/ReactToastify.css';
import { OffBoardingService } from 'services';
import moment from 'moment';
import UptimeChatWidget from 'components/UptimeChatWidget';
import { useTranslation } from 'react-i18next';
import 'react-notifications/lib/notifications.css';

/**
 * Switch routes for the application.
 *
 * Used for testing purposes as our testing utility
 * functions override `<Router>` with a provided history.
 */

const App: React.FC = () => {
  const dispatch = useDispatch();
  const { currentUser } = useSelector(
    (state: RootState) => state.baseReducer,
  );
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [, setShowFeedback] = useState<boolean>(false);
  // const [isShowAuthenTemplate, setIsShowAuthenTemplate] = useState<boolean>(false);
  const [loginMode, setLoginMode] = useState('');
  const [, setGlobalContentLanguage] = useContentLanguage();
  const isAuthenticatedByAzure = AppService.isAuthenticatedByAzure();
  const isAuthenticatedByOidc = AppService.isAuthenticatedByOidc();
  const [isNewJoiner, setIsNewJoiner] = useState<boolean>(false);
  const [isBeforeStartAccessDate, setIsBeforeStartAccessDate] = useState<boolean>(false);
  const [isLastWorkingDay, setIsLastWorkingDay] = useState<boolean>(false);
  const [isOverLastAccessDate, setIsOverLastAccessDate] = useState<boolean>(false);
  const [isNewJoinerLoading, setIsNewJoinerLoading] = useState<boolean>(true);
  const userPreferences = useSelector(
    ({ userPreferencesReducer }) =>
      userPreferencesReducer.userPreferences as UserPreferences,
  );
  const history = useHistory();

  useTranslation('errorMessages');

  const { permission, generalConfig, systemConfig } = useSelector(
    (state: RootState) => state.configReducer,
  );
  const { isOnBehalf } = useSelector(
    (state: RootState) => state.onBehalfReducer,
  );
  const queryParams = queryString.parse(window.location.search)?.customer;

  const { chatWidget, userFeedback, warningITARFedRamp } = permission;
  const isCheckChatWidget = checkEnabledFeature(chatWidget);
  const isCheckUserFeedback = checkEnabledFeature(userFeedback);
  const { getSystemDateTimeFormat } = useSystemConfiguration();
  const [oidcAuthen, setOidcAuthen] = useState(null);

  useEffect(() => {
    setLoginMode(sessionStorage.getItem(AUTH.AUTH_BY));
  }, []);

  useEffect(() => {
    (() => {
      if (!isAuthenticated) {
        checkAuthenStatus();
      } else {
        bootstrapConfig();
      }
    })();
  }, [isAuthenticated]);

  useEffect(() => {
    if(queryParams) {
      localStorage.setItem(CONFIG_CLIENT.CLIENT_STORAGE, queryParams)
    }
  },[queryParams]);

  useEffect(() => {
    const hasUserPreferencesData = Object.keys(userPreferences).length > 0;
    if (!hasUserPreferencesData) return;

    const localLanguage = localStorage.getItem(LANGUAGE_KEY);
    const userLanguage = userPreferences.preferences?.language;
    const languageCode = (localLanguage ||
      userLanguage ||
      'en') as LanguageCode;
    setGlobalContentLanguage(languageCode);
    localStorage.setItem(LANGUAGE_KEY, languageCode);

    UserService.saveTimeFormat(
      userPreferences.preferences?.time_format ||
        EDIT_PROFILE.DEFAULT_TIME_FORMAT,
    );
  }, [userPreferences]);

  useEffect(() => {
    if (generalConfig?.uptimeApimServices) {
      dispatch(ConfigAction.getNotificationConfig());
      dispatch(ConfigAction.getStaticPageConfig());
    }
  }, [generalConfig?.uptimeApimServices]);

  useEffect(() => {
    if (!!generalConfig?.version && !currentUser) {
      getAuthenticatedWrapper();
    }
  }, [generalConfig?.version, currentUser]);

  useEffect(() => {
    if (!generalConfig?.version || !currentUser) return;

    getPwChangeDate();
    if (generalConfig?.isAppInsightEnabled) {
      addDecodedScriptToHead(generalConfig?.appInsight?.code);
    };
    if (generalConfig?.isAllowedTracking) {
      initAppInsightInstance(generalConfig?.appInsight);
    };

    const result = generalConfig?.version;
    localStorage.setItem(PORTAL_VERSION, result || '');
  }, [generalConfig?.version, generalConfig?.isAllowedTracking, generalConfig?.appInsight, currentUser, generalConfig?.isAppInsightEnabled]);

  useEffect(() => {
    if (!!currentUser?.uid) {
      dispatch(userPreferencesAction.getUserPreferences(currentUser.uid));
      const localLanguage = localStorage.getItem(LANGUAGE_KEY);
      const currentUserLanguage = currentUser.chosen_language;
      const languagee = (localLanguage ||
        currentUserLanguage ||
        'en') as LanguageCode;
      setGlobalContentLanguage(languagee);
      localStorage.setItem(LANGUAGE_KEY, languagee);
    }
  }, [currentUser?.uid]);

  // const device = useDevice();
  const [isPilotMode, setIsPilotMode] = useState(true);
  const [isLoading, setIsloading] = useState(true);
  const [, setIsAuthenticating] = useState(true);

  if (isAuthenticatedByAzure && msalInstance) {
    if (
      !msalInstance.getActiveAccount() &&
      msalInstance.getAllAccounts().length > 0
    ) {
      // Account selection logic is app dependent. Adjust as needed for different use cases.
      msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
    }

    // Optional - This will update account state if a user signs in from another tab or window
    // msalInstance.enableAccountStorageEvents();

    msalInstance.addEventCallback((event: any) => {
      if (
        (event.eventType === EventType.LOGIN_SUCCESS ||
          event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
        event.payload.account
      ) {
        const account = event.payload.account;
        msalInstance.setActiveAccount(account);
      }
    });
  }
  loadGeInspiraFont();
  const checkPilot = async (userName: string) => {
    const checkUserPilot = await UserService.checkUserPilotMode(userName);
    return checkUserPilot.data?.success;
  };
  const setCurrentUser = data => {
    dispatch(BaseAction.setCurrentUser(data));
  };
  
  const bootstrapConfig = () => {
    dispatch(ConfigAction.getGeneralConfig());
    dispatch(marketplaceAction.getMarketplaceTheme());
  };

  const getOktaUserInfo = async (
    accessToken?: AccessToken,
    idToken?: IDToken,
  ) => {
    try {
      return await oktaAuth.token.getUserInfo(accessToken, idToken);
    } catch {
      const { accessToken, idToken } = await oktaAuth.token.renewTokens();
      return await getOktaUserInfo(accessToken, idToken);
    }
  };
  const getAuthenticatedWrapper = async () => {
    if (isAuthenticatedByOidc) {
      if (isAuthenticated) {
        const dataUser = localStorage.getItem(AUTH.CURRENT_USER);
        if (!!dataUser) {

          let account = JSON.parse(dataUser);

          account = {
            ...account, 
            user_name: generalConfig?.suffixUserCustomDomain ? `${account?.username}${generalConfig?.suffixUserCustomDomain}`: account?.username
          }          
          checkAuthenticated(isAuthenticated, account);
        }
      } else {
        setIsAuthenticating(false);
      }
    } else if (isAuthenticatedByAzure) {
      const account = msalInstance.getActiveAccount();
      if (account?.username && isAuthenticated) {
        checkAuthenticated(isAuthenticated, {
          ...account,
          email: account.username,
          user_name: generalConfig?.suffixUserCustomDomain ? `${account?.username}${generalConfig?.suffixUserCustomDomain}`: account?.username,
        });
      } else {
        setIsAuthenticating(false);
      }
    } else {
      oktaAuth
        .isAuthenticated()
        .then(async res => {
          if (res) {
            const userInfo = await getOktaUserInfo();
            checkAuthenticated(res, {
              ...userInfo,
              user_name: generalConfig?.suffixUserCustomDomain ? `${userInfo?.username}${generalConfig?.suffixUserCustomDomain}`: userInfo?.username
            });
          } else {
            setIsAuthenticating(false);
          }
        })
        .catch(() => setIsAuthenticating(false));
    }
  };
  const getPwChangeDate = async () => {
    try {
      const pwChangeDate = await UserService.getPwChangeDate(
        generalConfig?.apiGraphAzureUrl,
      );
      if (!!pwChangeDate) {
        localStorage.setItem(AUTH.PASSWORD_CHANGE_DATE, pwChangeDate);
      }
    } catch (error) {}
  };
  const checkAuthenticated = async (res, userInfo) => {
    // oktaAuth.isAuthenticated().then(async res => {
    if (!res) {
      setCurrentUser(null);
      const warningKey = warningITARFedRamp?.key;
      warningKey && localStorage.setItem(warningKey, 'true');
      localStorage.setItem(OPEN_HOME_PAGE_BANNER, 'true');
    } else {
      setIsloading(true);
      setIsNewJoinerLoading(true);
      let currentUsr;
      const offboardingUser = await getOffboardingUser(userInfo.email);
      const checkOverLastAccessDate = generalConfig?.leaver?.isLeaverEnabled ? handleCheckLastDate(offboardingUser, 'last_access_date') : false;
      const checkLastWorkingDate = generalConfig?.leaver?.isLeaverEnabled ? handleCheckLastDate(offboardingUser, 'last_working_date') : false;
      const checkNewJoiner = generalConfig?.newJoiner?.isNewJoinerAllowed ? await checkNewJoinerByEmail(userInfo.email) : false;

      if (generalConfig?.newJoiner?.isNewJoinerAllowed) {
        setIsNewJoiner(checkNewJoiner?.result);
        currentUsr = checkNewJoiner?.result
          ? await getNewJoinerInfoByEmail(userInfo.email)
          : await getUserProfile(userInfo.email, userInfo.user_name, generalConfig?.queryUserByUserName);
        if(currentUsr?.start_access_date){
          const isCheckBeforeStartAccessDate = moment().isBefore(moment(currentUsr?.start_access_date));
          setIsBeforeStartAccessDate(isCheckBeforeStartAccessDate);
        }
        const validUserCheck = checkNewJoiner?.result ? false : checkLastWorkingDate;
        setIsLastWorkingDay(validUserCheck);
        setIsOverLastAccessDate(checkOverLastAccessDate);
        currentUsr = { ...currentUsr, new_joiner: checkNewJoiner?.result, role: currentUsr.isManager ? 'MANAGER' : 'MEMBER', lastWorkingDate: validUserCheck, ...(generalConfig?.asdChatWidget?.isUseTokenEmail && { tokenEmail: userInfo.email })};
      } else {
        currentUsr = await getUserProfile(userInfo.email, userInfo.user_name, generalConfig?.queryUserByUserName);
        setIsLastWorkingDay(checkLastWorkingDate);
        currentUsr = { ...currentUsr, new_joiner: checkNewJoiner, role: currentUsr.isManager ? 'MANAGER' : 'MEMBER', lastWorkingDate: checkLastWorkingDate, ...(generalConfig?.asdChatWidget?.isUseTokenEmail && { tokenEmail: userInfo.email })};
      }
      setIsOverLastAccessDate(checkOverLastAccessDate);

      if(currentUsr?.lockedOut) {
        setCurrentUser(currentUsr);
        setIsloading(false);
        setIsNewJoinerLoading(false);
        history.push(PilotModePath);
        return;
      }

      setIsNewJoinerLoading(false);
      const userName = currentUsr?.user_name;
      const statusCheckPilot =
        currentUsr?.new_joiner || (await checkPilot(userName));
      setIsPilotMode(!!statusCheckPilot);

      if (isAuthenticatedByAzure || isAuthenticatedByOidc) {
        setCurrentUser(currentUsr);
      } else {
        const dataOkta = {
          altCompanyCode: userInfo.altCompanyCode,
          cscCountry: userInfo.cscCountry,
          cscCountry2: userInfo.cscCountry2,
          cscUserType: userInfo.cscUserType,
        };

        setCurrentUser({ ...currentUsr, ...dataOkta });
      }

      if (
        statusCheckPilot === false &&
        window.location.pathname !== PilotModePath
      ) {
        window.location.href = PilotModePath;
      }
      setIsloading(false);
    }
    setIsAuthenticated(res);
    setIsAuthenticating(false);
  };

  const checkAuthenStatus = () => {
    if (isAuthenticatedByOidc) {
      if (isAuthenticated) {
        setIsAuthenticated(true);
      } else {
        // reserve original pathname when user access specific link and still doesn't log-in yet
        const isHomePath = window.location.pathname === HomePath;
        if(!isHomePath) {
          sessionStorage.setItem('original_path', window.location.pathname);
        }
        setIsAuthenticated(false);
      }
    } else if (isAuthenticatedByAzure) {
      const account = msalInstance.getActiveAccount();
      if (account?.username && isAuthenticated) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    } else {
      oktaAuth
        ?.isAuthenticated()
        .then(async res => {
          if (res) {
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
          }
        })
        .catch(() => setIsAuthenticated(false));
    }
  };

  const getOffboardingUser = async (email: string) => {
    try {
      if (!generalConfig?.leaver?.isLeaverEnabled) return null;
      return await OffBoardingService.getOffboardingStatus(email);
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    if(!currentUser?.uid) return;
    (async () => {
      try {
        dispatch(ConfigAction.setLoading(true));
        const configKey = isOnBehalf
          ? CONFIG.PERMISSION_ABO.KEY
          : CONFIG.PERMISSION.KEY;
        const configLabel = isOnBehalf
          ? CONFIG.PERMISSION_ABO.LABEL
          : CONFIG.PERMISSION.LABEL;
        const permission = await getPermission({ configKey, configLabel });
        dispatch(ConfigAction.setPermissionConfigSuccess(permission));
      } catch (error) {
        console.error(error);
      }
    })();
  }, [isOnBehalf, !currentUser?.uid]);

  useEffect(() => {
    (async () => {
      if(!generalConfig?.version) return;
      getSystemDateTimeFormat();
    })();
  }, [systemConfig, generalConfig?.isUseSystemConfig, generalConfig?.version]);

  const appInsights = useAppInsightsContext();
  const trackingPath = localStorage.getItem(TRACKING_PATH);
  const trackPermalinkPathUrl = useTrackEvent<any>(
    appInsights,
    'Permalink Path',
    { permaLinkPathCount: 'This tracking from re-render component & initial event!' }
  );
  const trackOldPathUrl = useTrackEvent<any>(
    appInsights,
    'Old Path',
    { oldPathCount: 'This tracking from re-render component & initial event!'}
  );


  useEffect(() => {
    if(!!trackingPath && !!generalConfig?.version) {
      const data = JSON.parse(trackingPath);
      const { url, fromPermalinkUrl } = data;
      fromPermalinkUrl ? trackPermalinkPathUrl({ permaLinkPathCount: url }) : trackOldPathUrl({ oldPathCount: url });
    }
  }, [trackingPath, generalConfig?.version]);

  if (!loginMode) {
    return <></>;
  }

  return (
    <React.Fragment>
      <StyledAppContainer>
        {!!generalConfig?.staticImage?.favicon && (
          <Helmet>
            <link
              rel="icon"
              href={generalConfig?.staticImage?.favicon}
              type="image/svg+xml"
            />
          </Helmet>
        )}
        <LoadingScreenWithBackdrop
          isOpen={isLoading || !generalConfig?.version}
        />
        <SupportedLanguagesContainer>
          <OnBehalfContainer>
            <MarketplaceContainer>
              {isPilotMode && (
                <TopNav
                  isAuthenticated={isAuthenticated}
                  isNewJoiner={isNewJoiner}
                  isBeforeStartAccessDate={isBeforeStartAccessDate}
                  isNewJoinerLoading={isNewJoinerLoading}
                  isLastWorkingDay={isLastWorkingDay}
                  oidcAuthen={oidcAuthen}
                />
              )}
              <StyledBody>
                <StyledContentWrapper>
                  <ScrollToTop/>

                  <ErrorBoundary>
                    <AuthenTemplate
                      checkAuthenticated={bootstrapConfig}
                      onAuthenticatedSuccess={status => {
                        setIsAuthenticated(status);
                      }}
                      oidcAuthen={dataFunc => {
                        setOidcAuthen(dataFunc);
                      }}
                    >
                      <Suspense fallback={<LoadingScreen />}>
                        <RouterProvider
                          isAuthenticatedByAzure={isAuthenticatedByAzure}
                          isAuthenticatedByOkta={!isAuthenticatedByAzure && !isAuthenticatedByOidc}
                          isPilotMode={isPilotMode}
                          setShowFeedback={setShowFeedback}
                          isNewJoiner={isNewJoiner}
                          isBeforeStartAccessDate={isBeforeStartAccessDate}
                          isLastWorkingDay={isLastWorkingDay}
                          isOverLastAccessDate={isOverLastAccessDate}
                        />
                      </Suspense>
                    </AuthenTemplate>
                  </ErrorBoundary>
                </StyledContentWrapper>

                {isPilotMode && !isNewJoiner && !isNewJoinerLoading && (
                  <ThirdPartyScript showFeedback={!!isCheckUserFeedback} />
                )}

                <ChatWidgetComponent enabled={isPilotMode && isCheckChatWidget && !isNewJoiner && !isNewJoinerLoading && !isLastWorkingDay && !isOverLastAccessDate} />

              </StyledBody>
              <UptimeChatWidget />
              <Footer />
              <NotificationContainer />
            </MarketplaceContainer>
          </OnBehalfContainer>
        </SupportedLanguagesContainer>
      </StyledAppContainer>
    </React.Fragment>
  );
};
export default App;
